/* ============================================
   TURBO THEME — GLOBAL JS
   ============================================ */

'use strict';

// ---- Utilities ----
const Turbo = {
  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const value = (cents / 100).toFixed(2);
    const moneyFormat = format || window.Shopify.moneyFormat || '€{{amount}}';
    return moneyFormat.replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
  },

  debounce(fn, wait = 300) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  on(el, event, fn, opts = {}) {
    if (!el) return;
    el.addEventListener(event, fn, opts);
    return () => el.removeEventListener(event, fn, opts);
  },

  qs(selector, parent = document) {
    return parent.querySelector(selector);
  },

  qsa(selector, parent = document) {
    return [...parent.querySelectorAll(selector)];
  },

  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(`turbo:${name}`, { detail, bubbles: true }));
  },

  on$(name, fn) {
    document.addEventListener(`turbo:${name}`, fn);
  }
};

window.Turbo = Turbo;

// ---- Header scroll effect ----
(function initHeader() {
  const header = Turbo.qs('.site-header');
  if (!header) return;

  const handleScroll = Turbo.debounce(() => {
    header.classList.toggle('scrolled', window.scrollY > 20);
  }, 50);

  window.addEventListener('scroll', handleScroll, { passive: true });
})();

// ---- Mobile menu ----
(function initMobileMenu() {
  const toggle = Turbo.qs('.header-hamburger');
  const menu = Turbo.qs('.mobile-menu');
  const overlay = Turbo.qs('#overlay');
  const close = Turbo.qs('.mobile-menu__close');

  function open() {
    menu?.classList.add('open');
    overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    menu?.classList.remove('open');
    overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  toggle?.addEventListener('click', open);
  close?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeMenu();
  });
})();

// ---- Search modal ----
(function initSearch() {
  const triggers = Turbo.qsa('[data-search-open]');
  const modal = Turbo.qs('.search-modal');
  const input = Turbo.qs('.search-input');
  const closeBtn = Turbo.qs('[data-search-close]');

  function open() {
    modal?.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => input?.focus(), 100);
  }

  function close() {
    modal?.classList.remove('open');
    document.body.style.overflow = '';
  }

  triggers.forEach(t => t.addEventListener('click', open));
  closeBtn?.addEventListener('click', close);
  modal?.addEventListener('click', e => { if (e.target === modal) close(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
})();

// ---- Accordion ----
(function initAccordions() {
  Turbo.qsa('.accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion__item');
      const isOpen = item.classList.contains('open');

      // Close all in same parent
      const parent = item.closest('.accordion');
      Turbo.qsa('.accordion__item', parent).forEach(el => el.classList.remove('open'));

      if (!isOpen) item.classList.add('open');
    });
  });
})();

// ---- Announcement bar dismiss ----
(function initAnnouncementBar() {
  const bar = Turbo.qs('.announcement-bar');
  const dismiss = Turbo.qs('.announcement-bar__close');

  if (!dismiss || !bar) return;

  if (sessionStorage.getItem('turbo_announcement_dismissed')) {
    bar.remove();
    return;
  }

  dismiss.addEventListener('click', () => {
    bar.style.maxHeight = bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
      bar.style.maxHeight = '0';
      bar.style.opacity = '0';
      bar.style.overflow = 'hidden';
    });
    setTimeout(() => bar.remove(), 400);
    sessionStorage.setItem('turbo_announcement_dismissed', '1');
  });
})();

// ---- Product gallery ----
(function initProductGallery() {
  const gallery = Turbo.qs('.product-gallery');
  if (!gallery) return;

  const mainImg = Turbo.qs('.product-gallery__main img', gallery);
  const thumbs = Turbo.qsa('.product-gallery__thumb', gallery);

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      const srcset = thumb.dataset.srcset;

      if (mainImg && src) {
        mainImg.src = src;
        if (srcset) mainImg.srcset = srcset;
        mainImg.style.opacity = '0';
        requestAnimationFrame(() => {
          mainImg.style.transition = 'opacity 0.25s ease';
          mainImg.style.opacity = '1';
        });
      }

      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });
})();

// ---- Variant selection ----
(function initVariants() {
  const form = Turbo.qs('form[data-product-form]');
  if (!form) return;

  const options = Turbo.qsa('.variant-option', form);
  const priceEl = Turbo.qs('.product-info__price .price--main');
  const addBtn = Turbo.qs('[data-add-to-cart]', form);
  let variants = [];

  try {
    variants = JSON.parse(form.dataset.variants || '[]');
  } catch (e) {}

  function getSelectedOptions() {
    const selected = {};
    Turbo.qsa('[data-option-name]', form).forEach(group => {
      const name = group.dataset.optionName;
      const active = Turbo.qs('.variant-option.selected', group);
      if (active) selected[name] = active.dataset.value;
    });
    return selected;
  }

  function findVariant(selected) {
    return variants.find(v =>
      v.options.every((opt, i) => opt === Object.values(selected)[i])
    );
  }

  options.forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-option-name]');
      Turbo.qsa('.variant-option', group).forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');

      const selected = getSelectedOptions();
      const variant = findVariant(selected);

      if (variant) {
        const input = Turbo.qs('[name="id"]', form);
        if (input) input.value = variant.id;

        if (priceEl && variant.price) {
          priceEl.textContent = Turbo.formatMoney(variant.price);
        }

        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available
            ? addBtn.dataset.addText || 'Add to cart'
            : addBtn.dataset.soldText || 'Sold out';
        }

        Turbo.emit('variant:changed', { variant });
        window.history.replaceState({}, '', `?variant=${variant.id}`);
      }
    });
  });
})();

// ---- Quantity input ----
(function initQty() {
  Turbo.qsa('.product-info__qty').forEach(wrap => {
    const input = Turbo.qs('.qty-input', wrap);
    const minus = Turbo.qs('.qty-btn--minus', wrap);
    const plus = Turbo.qs('.qty-btn--plus', wrap);

    minus?.addEventListener('click', () => {
      const val = parseInt(input.value);
      if (val > 1) input.value = val - 1;
    });

    plus?.addEventListener('click', () => {
      const val = parseInt(input.value);
      if (val < 99) input.value = val + 1;
    });

    input?.addEventListener('change', () => {
      let val = parseInt(input.value);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 99) val = 99;
      input.value = val;
    });
  });
})();

// ---- Toast notifications ----
window.TurboNotification = {
  show(message, type = 'success') {
    const el = Turbo.qs('#cart-notification');
    if (!el) return;

    el.innerHTML = `
      <div class="cart-notification__inner">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span>${message}</span>
      </div>
    `;

    el.classList.add('show');
    setTimeout(() => el.classList.remove('show'), 3500);
  }
};

// ---- Lazy load images ----
(function initLazyLoad() {
  if ('IntersectionObserver' in window) {
    const images = Turbo.qsa('img[data-src]');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.removeAttribute('data-src');
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '100px' });

    images.forEach(img => observer.observe(img));
  }
})();

// ---- Scroll reveal ----
(function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  Turbo.qsa('[data-reveal]').forEach(el => observer.observe(el));
})();
